import React from 'react';
import { cn } from './cn';
import { aileToplami, hexToRgba, MUFREDATLAR } from '../../lib/dersler';

// Ders seçici — "ÖSYM kitapçık bölümleri" (2026-10-21, kullanıcı kararı). Dersler basılı
// kitapçıktaki test bloklarının altında, ÖSYM sırasında durur; bant rengi o testin
// kitapçıktaki tema rengidir. Ayna: backend/src/lib/dersBaslik.js OSYM_BLOK_RENK.
const BANT = {
  lila: { zemin: '#DFCCE4', kenar: '#C7A9D0' },
  camgobegi: { zemin: '#BEE5EB', kenar: '#8FCCD6' },
  somon: { zemin: '#FCD1C2', kenar: '#EFAA93' },
  acikMavi: { zemin: '#BCE4F7', kenar: '#8CCBEA' },
  diger: { zemin: '#E2E8F0', kenar: '#CBD5E1' },
};

export const SINAV_BOLUMLERI = {
  TYT: [
    { ad: 'Türkçe', bant: 'lila', dersler: ['tyt_turkce'] },
    { ad: 'Sosyal Bilimler', bant: 'camgobegi', dersler: ['tyt_tarih', 'tyt_cografya', 'tyt_felsefe', 'tyt_din'] },
    { ad: 'Temel Matematik', bant: 'somon', dersler: ['tyt_matematik', 'tyt_geometri'] },
    { ad: 'Fen Bilimleri', bant: 'camgobegi', dersler: ['tyt_fizik', 'tyt_kimya', 'tyt_biyoloji'] },
  ],
  AYT: [
    { ad: 'Türk Dili ve Edebiyatı–Sosyal Bilimler-1', bant: 'camgobegi', dersler: ['ayt_edebiyat', 'ayt_tarih', 'ayt_cografya'], not: 'Tarih ve Coğrafya, Sosyal Bilimler-2 testinde de yer alır.' },
    { ad: 'Sosyal Bilimler-2', bant: 'lila', dersler: ['ayt_felsefe', 'ayt_mantik', 'ayt_psikoloji', 'ayt_sosyoloji', 'ayt_din'] },
    { ad: 'Matematik', bant: 'somon', dersler: ['ayt_matematik', 'ayt_geometri'] },
    { ad: 'Fen Bilimleri', bant: 'acikMavi', dersler: ['ayt_fizik', 'ayt_kimya', 'ayt_biyoloji'] },
  ],
};

// Görünür dersleri bölümlere dağıtır. Görünür dersi kalmayan bölüm düşer; hiçbir
// bölümde olmayan ders (ileride eklenen) "Diğer dersler"e gider, kaybolmaz.
export function bolumlereAyir(sinav, aileler) {
  const tanimli = SINAV_BOLUMLERI[sinav] || [];
  const kullanilan = new Set();
  const bolumler = tanimli.map((b) => {
    const uyeler = b.dersler.map((k) => aileler.find((a) => a.key === k)).filter(Boolean);
    uyeler.forEach((a) => kullanilan.add(a.key));
    return { ...b, aileler: uyeler };
  }).filter((b) => b.aileler.length > 0);
  const kalan = aileler.filter((a) => !kullanilan.has(a.key));
  if (kalan.length) bolumler.push({ ad: 'Diğer dersler', bant: 'diger', aileler: kalan });
  return bolumler;
}

// Ders kutusunun sayıları. mod: 'gizle' (satılanlar düşülür) | 'goster' (satılan payı
// ayrıca) | null (satılan bilgisi yok — toplam). ozet: ders-ozeti yanıtı
// { sayilar, maarif, satilan, satilanMaarif }.
export function dersKutuSayilari(aile, ozet, mod) {
  const toplam = aileToplami(aile, ozet.sayilar);
  const maarifTum = aileToplami(aile, ozet.maarif);
  const sat = mod ? aileToplami(aile, ozet.satilan) : 0;
  const satMaarif = mod ? aileToplami(aile, ozet.satilanMaarif) : 0;
  return {
    sayi: mod === 'gizle' ? toplam - sat : toplam,
    meb: Math.max(0, toplam - maarifTum - (sat - satMaarif)),
    maarif: Math.max(0, maarifTum - satMaarif),
    satilan: mod === 'goster' ? sat : 0,
  };
}

const sayi = (n) => Number(n || 0).toLocaleString('tr-TR');
const MEB_RENK = MUFREDATLAR[0].renk;
const MAARIF_RENK = MUFREDATLAR[1].renk;
const SATILAN_DESEN = 'repeating-linear-gradient(135deg, #EA580C 0px, #EA580C 2px, #FDBA74 2px, #FDBA74 4px)';

// Elle çizilmiş çizgi ikonlar (24×24, stroke = ders rengi). Emoji her işletim sisteminde
// farklı çizildiği için yerine geçti.
const IKON = {
  matematik: <><path d="M4 4v16h16" /><path d="M7 16c2.5-7 5-9 7-6s3 2 5-4" /></>,
  geometri: <><path d="M4 19h16L9 5Z" /><path d="M9 5v14" /><path d="M9 16h3v3" /></>,
  kimya: <><path d="M9 3h6" /><path d="M10 3v6.5L4.6 18.2A1.8 1.8 0 0 0 6.1 21h11.8a1.8 1.8 0 0 0 1.5-2.8L14 9.5V3" /><path d="M7 15h10" /></>,
  fizik: <><circle cx="12" cy="12" r="1.6" fill="currentColor" stroke="none" /><ellipse cx="12" cy="12" rx="9.5" ry="3.8" /><ellipse cx="12" cy="12" rx="9.5" ry="3.8" transform="rotate(60 12 12)" /><ellipse cx="12" cy="12" rx="9.5" ry="3.8" transform="rotate(-60 12 12)" /></>,
  biyoloji: <><path d="M7 3c0 4.5 10 4.5 10 9s-10 4.5-10 9" /><path d="M17 3c0 4.5-10 4.5-10 9s10 4.5 10 9" /><path d="M8 12h8M8.5 4.5h7M8.5 19.5h7" /></>,
  turkce: <><path d="M12 6.5C10 5 6.5 4.6 3.5 5.3V19c3-.7 6.5-.3 8.5 1.2 2-1.5 5.5-1.9 8.5-1.2V5.3c-3-.7-6.5-.3-8.5 1.2Z" /><path d="M12 6.5v13.7" /></>,
  edebiyat: <><path d="M19.5 4.5C12 4.5 8 9.5 7 16" /><path d="M19.5 4.5c.5 6.5-4.5 11-12 11.5" /><path d="M4.5 20 7 16" /></>,
  tarih: <><path d="M3.5 20.5h17" /><path d="M5 17.5h14" /><path d="M4 8h16L12 3.5Z" /><path d="M7 8v9.5M12 8v9.5M17 8v9.5" /></>,
  cografya: <><circle cx="12" cy="12" r="8.5" /><path d="M3.5 12h17" /><path d="M12 3.5c2.6 2.4 3.8 5.2 3.8 8.5s-1.2 6.1-3.8 8.5c-2.6-2.4-3.8-5.2-3.8-8.5S9.4 5.9 12 3.5Z" /></>,
  felsefe: <><path d="M5 19V6.5a2 2 0 0 1 2-2h10a2 2 0 0 1 2 2v7a2 2 0 0 1-2 2H8.5Z" /><path d="M10.3 8.4a1.8 1.8 0 1 1 2.6 1.6c-.6.3-.9.8-.9 1.4" /><circle cx="12" cy="13.4" r=".5" fill="currentColor" /></>,
  din: <><path d="M12 3a6 6 0 0 0 9 9 9 9 0 1 1-9-9Z" /><path d="M18 3.5v3M16.5 5h3" /></>,
  mantik: <><circle cx="12" cy="5" r="2" /><circle cx="6" cy="18" r="2" /><circle cx="18" cy="18" r="2" /><path d="M11 6.8 7 16.2M13 6.8l4 9.4M8 18h8" /></>,
  psikoloji: <><path d="M9 21v-3.5A7 7 0 1 1 18.5 11l1.5 3h-2v2.5a1.5 1.5 0 0 1-1.5 1.5H15v3" /><circle cx="12.5" cy="9.5" r="2" /></>,
  sosyoloji: <><circle cx="9" cy="8" r="3" /><path d="M3.5 19.5a5.5 5.5 0 0 1 11 0" /><circle cx="16.5" cy="9" r="2.4" /><path d="M15 14.2a4.5 4.5 0 0 1 6 4.3" /></>,
};

export function DersIkon({ dersKey, className, style }) {
  const ad = String(dersKey || '').replace(/^(tyt|ayt|lgs)_/, '');
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round"
      strokeLinejoin="round" aria-hidden="true" className={className} style={style}>
      {IKON[ad] || <circle cx="12" cy="12" r="7" />}
    </svg>
  );
}

function Cubuk({ meb, maarif, satilan }) {
  const t = meb + maarif + satilan;
  const pay = (n) => (t ? `${(n / t) * 100}%` : '0%');
  const dilim = 'h-full transition-[width] duration-300 motion-reduce:transition-none';
  return (
    <span className="flex h-1.5 w-full overflow-hidden rounded-full bg-slate-100" aria-hidden="true">
      <span className={dilim} style={{ width: pay(meb), backgroundColor: MEB_RENK }} />
      <span className={dilim} style={{ width: pay(maarif), backgroundColor: MAARIF_RENK }} />
      <span className={dilim} style={{ width: pay(satilan), backgroundImage: SATILAN_DESEN }} />
    </span>
  );
}

function DersHucresi({ aile, s, secili, onSec }) {
  const pasif = !aile.aktif;
  const etiket = `${aile.label}: ${sayi(s.sayi)} soru (MEB 2018 ${sayi(s.meb)}, Maarif ${sayi(s.maarif)}`
    + (s.satilan ? `, ${sayi(s.satilan)} satılan)` : ')');
  return (
    <button
      type="button"
      data-ders={aile.key}
      disabled={pasif}
      aria-pressed={secili}
      aria-label={etiket}
      title={pasif ? `${aile.label} — yakında` : etiket}
      onClick={() => !pasif && onSec(aile.key)}
      className={cn(
        'relative flex min-w-0 flex-col gap-2 border-l border-t border-slate-200/80 px-3.5 pb-2.5 pt-3 text-left transition-colors',
        'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset',
        pasif ? 'cursor-not-allowed bg-slate-50/70' : secili ? '' : 'bg-white hover:bg-slate-50'
      )}
      style={{
        ...(secili && !pasif ? { backgroundColor: hexToRgba(aile.renk, 0.08) } : null),
        '--tw-ring-color': hexToRgba(aile.renk, 0.55),
      }}
    >
      <span className="flex min-w-0 items-center gap-2">
        <DersIkon dersKey={aile.key} className={cn('h-[18px] w-[18px] shrink-0', pasif && 'opacity-40')} style={{ color: aile.renk }} />
        <span className="truncate text-[13px] font-semibold" style={{ color: secili && !pasif ? aile.renk : pasif ? '#94A3B8' : '#334155' }}>
          {aile.label}
        </span>
      </span>
      {pasif ? (
        <span className="text-xs text-slate-400">yakında</span>
      ) : (
        <>
          <span className="flex items-baseline gap-1.5">
            <span className="text-2xl font-bold leading-none tabular-nums text-slate-800">{sayi(s.sayi)}</span>
            {s.satilan > 0 && (
              <span data-testid="ders-satilan" className="text-[11px] font-semibold tabular-nums text-orange-600">−{sayi(s.satilan)}</span>
            )}
          </span>
          <span className="flex flex-col gap-1">
            <Cubuk meb={s.meb} maarif={s.maarif} satilan={s.satilan} />
            <span className="flex justify-between gap-2 text-[11px] font-semibold leading-none tabular-nums">
              <span style={{ color: MEB_RENK }}>{s.meb > 0 ? sayi(s.meb) : ''}</span>
              <span style={{ color: MAARIF_RENK }}>{s.maarif > 0 ? sayi(s.maarif) : ''}</span>
            </span>
          </span>
        </>
      )}
      {secili && !pasif && <span className="absolute inset-x-0 bottom-0 h-0.5" style={{ backgroundColor: aile.renk }} />}
    </button>
  );
}

// Lejant: çubuğun dilimlerini adlandırır. Satılan dilimi yalnız "göster" modunda var.
export function BolumLejanti({ mod }) {
  const kutu = 'inline-block h-2 w-3 rounded-sm';
  return (
    <span className="flex flex-wrap items-center gap-x-3 gap-y-1 text-[11px] text-slate-500">
      <span className="flex items-center gap-1.5"><span className={kutu} style={{ backgroundColor: MEB_RENK }} />MEB 2018</span>
      <span className="flex items-center gap-1.5"><span className={kutu} style={{ backgroundColor: MAARIF_RENK }} />Maarif 2024</span>
      {mod === 'goster' && (
        <span className="flex items-center gap-1.5"><span className={kutu} style={{ backgroundImage: SATILAN_DESEN }} />satılan</span>
      )}
    </span>
  );
}

export function SinavBolumleri({ sinav, aileler, aktifKey, onSec, ozet, satilanModu = null }) {
  return (
    <div className="flex flex-wrap gap-3">
      {bolumlereAyir(sinav, aileler).map((b) => {
        const n = b.aileler.length;
        const bant = BANT[b.bant] || BANT.diger;
        const kutular = b.aileler.map((a) => [a, dersKutuSayilari(a, ozet, satilanModu)]);
        const toplam = kutular.reduce((t, [a, s]) => t + (a.aktif ? s.sayi : 0), 0);
        return (
          <section
            key={b.ad}
            className="min-w-0 overflow-hidden rounded-lg border bg-white"
            style={{ flex: `${n} 1 ${n * 112}px`, borderColor: bant.kenar }}
          >
            <header className="flex items-baseline justify-between gap-3 px-3.5 py-1.5" style={{ backgroundColor: bant.zemin }} title={b.not}>
              <h3 className="truncate text-[13px] font-bold text-slate-800">{b.ad}</h3>
              <span className="shrink-0 text-xs font-semibold tabular-nums text-slate-700">{sayi(toplam)}</span>
            </header>
            {/* Sol/üst kenarlar -1px kaydırılıp kırpılır: hücre ayraçları yalnız hücreler
                ARASINDA kalır, sarma olsa da dış çerçeveyle çift çizgi oluşmaz. */}
            <div className="overflow-hidden">
              <div className="-ml-px -mt-px grid" style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(104px, 1fr))' }}>
                {kutular.map(([a, s]) => (
                  <DersHucresi key={a.key} aile={a} s={s} secili={a.key === aktifKey} onSec={onSec} />
                ))}
              </div>
            </div>
          </section>
        );
      })}
    </div>
  );
}

export default SinavBolumleri;
